import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import { MapPin, Loader2, ChevronRight, List, Map as MapIcon } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Custom marker icons
const createIcon = (minority) => new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="
    width: 32px; height: 32px; border-radius: 50%;
    background: ${minority ? '#C8A24A' : '#0A2F25'};
    border: 3px solid white;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    display: flex; align-items: center; justify-content: center;
  ">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5">
      <path d="M3 21h18M5 21V7l8-4v18M13 21V3l6 4v14"/>
    </svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32]
});

const CourseMapPage = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('map');

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API_URL}/courses/map`);
        setCourses(res.data.courses || []);
      } catch (err) {
        console.error('Failed to fetch courses:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const coursesWithGeo = courses.filter(c => c.geo?.lat && c.geo?.lng);
  const minorityCount = courses.filter(c => c.minority_owned).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-bone pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-fairway" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone pt-24 pb-20">
      {/* Header */}
      <div className="bg-deep-green py-12 mb-8">
        <div className="container-padding flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-gold font-semibold tracking-widest uppercase text-sm mb-2">Discover Courses</p>
            <h1 className="font-display font-bold text-3xl md:text-4xl text-white mb-3" data-testid="course-map-title">
              Course Map
            </h1>
            <p className="text-cream/70 font-body">
              {courses.length} courses across the U.S. — {minorityCount} minority-owned
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant={viewMode === 'map' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('map')}
              className={viewMode === 'map' ? 'btn-gold' : 'border-white/30 text-cream hover:bg-white/10'}
              data-testid="map-view-btn"
            >
              <MapIcon className="w-4 h-4 mr-1" /> Map
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
              className={viewMode === 'list' ? 'btn-gold' : 'border-white/30 text-cream hover:bg-white/10'}
              data-testid="list-view-btn"
            >
              <List className="w-4 h-4 mr-1" /> List
            </Button>
          </div>
        </div>
      </div>

      <div className="container-padding">
        {/* Legend */}
        <div className="flex items-center gap-6 mb-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-gold border-2 border-white shadow" />
            <span className="text-muted-foreground">Minority-Owned</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-deep-green border-2 border-white shadow" />
            <span className="text-muted-foreground">Historic / Community</span>
          </div>
        </div>

        {viewMode === 'map' ? (
          /* Map View */
          <div className="rounded-sm overflow-hidden shadow-lg border border-gray-200" style={{ height: '600px' }} data-testid="course-map-container">
            {coursesWithGeo.length > 0 ? (
              <MapContainer
                center={[37.5, -82]}
                zoom={5}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {coursesWithGeo.map(course => (
                  <Marker
                    key={course.course_id || course.id}
                    position={[course.geo.lat, course.geo.lng]}
                    icon={createIcon(course.minority_owned)}
                  >
                    <Popup>
                      <div className="min-w-[200px]">
                        <h3 className="font-bold text-sm text-deep-green mb-1">{course.name}</h3>
                        <p className="text-xs text-gray-600 mb-1">{course.city}, {course.state}</p>
                        {course.minority_owned && (
                          <span className="inline-block bg-gold/20 text-gold text-xs px-2 py-0.5 rounded mb-2">Minority-Owned</span>
                        )}
                        {course.pricing_notes && (
                          <p className="text-xs text-gray-500 mb-2 line-clamp-2">{course.pricing_notes}</p>
                        )}
                        <a href={`/courses/${course.course_id || course.id}`} className="text-xs text-gold font-semibold hover:underline">
                          View Details &rarr;
                        </a>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            ) : (
              <div className="h-full flex items-center justify-center bg-gray-100">
                <p className="text-muted-foreground">No courses with location data</p>
              </div>
            )}
          </div>
        ) : (
          /* List View */
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map(course => (
              <Link key={course.course_id || course.id} to={`/courses/${course.course_id || course.id}`}>
                <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-all h-full group" data-testid="map-course-card">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-display font-bold text-deep-green group-hover:text-gold transition-colors">{course.name}</h3>
                      <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gold shrink-0 mt-1" />
                    </div>
                    <p className="text-muted-foreground text-sm flex items-center gap-1 mb-2">
                      <MapPin className="w-3 h-3" /> {course.city}, {course.state}
                    </p>
                    {course.minority_owned && <Badge className="bg-gold/20 text-gold text-xs">Minority-Owned</Badge>}
                    {course.pricing_notes && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{course.pricing_notes}</p>}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseMapPage;
